import LoadingSpinner from "../../components/loadingSpinner/loadingSpinner";
import TonalResultPanel from "../../components/tonalResultPanel/tonalResultPanel";
import VerbalResultPanel from "../../components/verbalResultPanel/verbalResultPanel";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import AnalysisModal from "../../modal/analysisModal/analysisModalBase";
import VerbalAnalysisModal from "../../modal/analysisModal/analysisModal";
import SendEmailResultModal from "../../modal/sendEmailResultModal/sendEmailResultModal";
import type { TestParameters, UserResultsDetailPanelModel } from "../../type/result";
import TonalParamsBadges from "../../components/tonalResultPanel/tonalParamsBadges";
import { logActivity } from "../../utils/logActivity";
import { useResultExport } from "../../utils/useResultExport";
import { useAuthUser } from "../../utils/useAuthUser";
import { useAnalysisModal } from "../../utils/useAnalysisModal";
import { canAnalyzeVerbalResult } from "../../utils/hearingResultAnalysis";
import { useState } from "react";
import { useIpLocation } from "../../utils/useIpLocation";
import { parseUserAgent } from "../../utils/parseUserAgent";
import "./userResultsChart.scss";

export default function UserResultsDetailPanel({
  selection,
  detailLoading,
  formatDate,
  onAnalysisSaved
}: UserResultsDetailPanelModel) {
  const { t } = useI18n();
  const user = useAuthUser();

  const toneItem     = selection?.kind === "tone"   ? selection.item   : null;
  const parsedResult = selection?.kind === "tone"   ? selection.parsed : null;
  const verbalItem   = selection?.kind === "verbal" ? selection.item   : null;
  const verbalResult = selection?.kind === "verbal" ? selection.parsed : null;

  const toneTestParams = (() => {
    if (!toneItem) return null;
    try { return JSON.parse(toneItem.test_params) as TestParameters; } catch { return null; }
  })();
  const verbalTestParams = (() => {
    if (!verbalItem) return null;
    try { return JSON.parse(verbalItem.test_params) as TestParameters; } catch { return null; }
  })();

  const { isAnalysisModalOpen, handleCloseAnalysisModal, handleAnalyzeResults } = useAnalysisModal(parsedResult, toneItem?.idtonalresult);
  //const showAnalysisButton = user?.parameters?.["open-ai"]?.value === true;
  const showAnalysisButton = true;

  const toneIpLocation   = useIpLocation(user?.admin === true ? toneItem?.ip_address   : null);
  const verbalIpLocation = useIpLocation(user?.admin === true ? verbalItem?.ip_address : null);

  const [isVerbalAnalysisModalOpen, setIsVerbalAnalysisModalOpen] = useState(false);

  const [emailModal, setEmailModal] = useState<{
    open: boolean;
    capturePanel: (() => Promise<HTMLCanvasElement>) | null;
    idtonalresult: number | null;
    idverbalresult: number | null;
    fileBaseName: string;
  }>({ open: false, capturePanel: null, idtonalresult: null, idverbalresult: null, fileBaseName: "" });

  const {
    isExportMenuOpen,
    setIsExportMenuOpen,
    isExporting,
    exportFormat,
    exportMenuRef,
    resultPanelRef,
    handleExport,
    captureResultPanel
  } = useResultExport({
    panelSelector: ".user-results__charts",
    actionsClassName: "result-header__actions",
    extraFooterText: toneItem?.ai_analysis,
    getFileBaseName: () => `Wynik audiometrii tonalnej ${toneItem ? formatDate(toneItem.created_at) : "export"}`,  
    onExported: (format) => logActivity("download_result", window.location.pathname, `Pobrano wyniki audiometrii tonalnej jako ${format.toUpperCase()}`, toneItem?.idtonalresult)
  });

  const {
    isExportMenuOpen: isVerbalExportMenuOpen,
    setIsExportMenuOpen: setIsVerbalExportMenuOpen,
    isExporting: isVerbalExporting,
    exportFormat: verbalExportFormat,
    exportMenuRef: verbalExportMenuRef,
    resultPanelRef: verbalResultPanelRef,
    handleExport: handleVerbalExport,
    captureResultPanel: captureVerbalResultPanel
  } = useResultExport({
    panelSelector: ".verbal-results",
    actionsClassName: "result-header__actions",
    getFileBaseName: () => `Wynik audiometrii słownej (${verbalItem ? formatDate(verbalItem.created_at) : "export"})`,  
    onExported: (format) => logActivity("download_result", window.location.pathname, `Pobrano wyniki audiometrii słownej jako ${format.toUpperCase()}`, verbalItem?.idverbalresult)
  });

  return (
    <div className="app-content__panelright">
      {!selection && (
        <div className="panel user-results__empty card border-0">
          <div className="card-body p-4 d-flex flex-column align-items-center justify-content-center">
            <i className="bi bi-bar-chart-line user-results__empty-icon mb-3" />
            <h3 className="mb-2">{t("Wybierz badanie")}</h3>
            <p className="mb-0">{t("Kliknij element z listy, aby zobaczyć wykresy audiogramu.")}</p>
          </div>
        </div>
      )}

      {selection?.kind === "tone" && toneItem && (
        <>
          <TonalResultPanel
            result={parsedResult}
            resultPanelRef={resultPanelRef}
            exportMenuRef={exportMenuRef}
            panelClassName="user-results__charts"
            subtitle={(() => {
              let testParams: TestParameters | null = null;
              try { testParams = JSON.parse(toneItem.test_params) as TestParameters; } catch { /* ignore */ }
              return (
                <>
                  <p className="mb-0 user-results__subtitle">
                    <i className="bi bi-calendar3 me-1" />
                    {formatDate(toneItem.created_at)}
                    <span className="ms-3 user-results__id-badge"></span>
                  </p>
                  {testParams && <TonalParamsBadges params={testParams} />}
                  {user?.admin === true && (
                    <p className="mb-0 mt-1 user-results__admin-meta">
                      <i className="bi bi-geo-alt me-1" />
                      <span>
                        {toneItem.ip_address}
                        {toneIpLocation && (
                          <span className="ms-1 user-results__admin-meta__location">({toneIpLocation})</span>
                        )}
                      </span>
                      <span
                        className="ms-3 user-results__admin-meta__agent"
                        title={toneItem.web_agent}
                      >
                        {parseUserAgent(toneItem.web_agent)}
                      </span>
                    </p>               
                  )}
                  {user?.admin === true && toneItem.description && (
                    <p className="mb-0 mt-1 user-results__admin-meta">
                      <i className="bi bi-card-text me-1" />
                      <span
                        className="user-results__admin-meta__agent"
                        title={toneItem.description}
                      >
                        {toneItem.description}
                      </span>
                    </p>   
                    )}  
                </>
              );
            })()}
            isExportMenuOpen={isExportMenuOpen}
            isExporting={isExporting}
            exportFormat={exportFormat}
            isExportDisabled={detailLoading || !parsedResult || isExporting}
            showAnalysisButton={showAnalysisButton}
            isAnalysisDisabled={detailLoading || !parsedResult || isExporting}
            onToggleExportMenu={() => setIsExportMenuOpen(prev => !prev)}
            onExport={(format) => void handleExport(format)}
            onAnalyze={handleAnalyzeResults}
            onEmailResult={() => {
              setIsExportMenuOpen(false);
              setEmailModal({
                open: true,
                capturePanel: captureResultPanel,
                idtonalresult: toneItem.idtonalresult,
                idverbalresult: null,
                fileBaseName: `Wynik audiometrii tonalnej ${formatDate(toneItem.created_at)}`,
              });
            }}
          >
            {detailLoading && (
              <LoadingSpinner className="user-results__state" label={t("Ładowanie wykresu…")} />
            )}
            {!detailLoading && !parsedResult && (
              <div className="user-results__state user-results__state--error">
                <i className="bi bi-exclamation-triangle-fill me-2" />
                {t("Nie udało się odczytać danych wykresu.")}
              </div>
            )}
          </TonalResultPanel>

          <AnalysisModal
            isOpen={isAnalysisModalOpen}
            onClose={handleCloseAnalysisModal}
            parsedResult={parsedResult}
            demographics={{ ageCategory: toneTestParams?.ageCategory, headphoneType: toneTestParams?.headphoneType }}
            id={toneItem.idtonalresult}
            cachedAnalysis={toneItem.ai_analysis || null}
            onAnalysisSaved={(id, analysis) => onAnalysisSaved?.(id, "tone", analysis)}
          />
        </>
      )}

      {selection?.kind === "verbal" && verbalItem && (
        <>
          <VerbalResultPanel
            result={verbalResult}
            resultPanelRef={verbalResultPanelRef}
            exportMenuRef={verbalExportMenuRef}
            subtitle={(() => {
              let verbalParams: TestParameters | null = null;
              try { verbalParams = JSON.parse(verbalItem.test_params) as TestParameters; } catch { /* ignore */ }
              return (
                <>
                  <p className="mb-0 user-results__subtitle">
                    <i className="bi bi-calendar3 me-1" />
                    {formatDate(verbalItem.created_at)}
                  </p>
                  {verbalParams && <TonalParamsBadges params={verbalParams} />}
                  {user?.admin === true && (
                    <p className="mb-0 mt-1 user-results__admin-meta">
                      <i className="bi bi-geo-alt me-1" />
                      <span>
                        {verbalItem.ip_address}
                        {verbalIpLocation && (
                          <span className="ms-1 user-results__admin-meta__location">({verbalIpLocation})</span>
                        )}
                      </span>
                      <span
                        className="ms-3 user-results__admin-meta__agent"
                        title={verbalItem.web_agent}
                      >
                        {parseUserAgent(verbalItem.web_agent)}
                      </span>
                    </p>
                  )}
                </>
              );
            })()}
            isExportMenuOpen={isVerbalExportMenuOpen}
            isExporting={isVerbalExporting}
            exportFormat={verbalExportFormat}
            isExportDisabled={detailLoading || !verbalResult || isVerbalExporting}
            showAnalysisButton={showAnalysisButton}
            isAnalysisDisabled={detailLoading || !verbalResult || isVerbalExporting}
            onToggleExportMenu={() => setIsVerbalExportMenuOpen(prev => !prev)}
            onExport={(format) => void handleVerbalExport(format)}
            onAnalyze={() => {
              if (!verbalResult || !canAnalyzeVerbalResult(verbalResult)) return;
              logActivity("analyze_results", window.location.pathname, "Otwarto analizę wyników", verbalItem.idverbalresult);
              setIsVerbalAnalysisModalOpen(true);
            }}
            onEmailResult={() => {
              setIsVerbalExportMenuOpen(false);
              setEmailModal({
                open: true,
                capturePanel: captureVerbalResultPanel,
                idtonalresult: null,
                idverbalresult: verbalItem.idverbalresult,
                fileBaseName: `Wynik audiometrii słownej (${formatDate(verbalItem.created_at)})`,
              });
            }}
          >
            {detailLoading && (
              <LoadingSpinner className="user-results__state" label={t("Ładowanie wykresu…")} />
            )}
            {!detailLoading && !verbalResult && (
              <div className="user-results__state user-results__state--error">
                <i className="bi bi-exclamation-triangle-fill me-2" />
                {t("Nie udało się odczytać danych wykresu.")}
              </div>
            )}
          </VerbalResultPanel>

          <VerbalAnalysisModal
            isOpen={isVerbalAnalysisModalOpen}
            onClose={() => setIsVerbalAnalysisModalOpen(false)}
            parsedResult={verbalResult}
            demographics={{ ageCategory: verbalTestParams?.ageCategory, headphoneType: verbalTestParams?.headphoneType }}
            id={verbalItem.idverbalresult}
            cachedAnalysis={verbalItem.ai_analysis || null}
            onAnalysisSaved={(id, analysis) => onAnalysisSaved?.(id, "verbal", analysis)}
          />
        </>
      )}

      <SendEmailResultModal
        isOpen={emailModal.open}
        onClose={() => setEmailModal(prev => ({ ...prev, open: false }))}
        captureResultPanel={emailModal.capturePanel}
        fileBaseName={emailModal.fileBaseName}
        idtonalresult={emailModal.idtonalresult}
        idverbalresult={emailModal.idverbalresult}
        iduser={user?.iduser ?? null}
      />
    </div>
  );
}